import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

// Busca "/" por cima de qualquer tela (decisão B7). Aqui só o aberto/fechado e a volta do foco:
// o que se busca é da features/busca. Fechar devolve o cursor ao campo de onde ela saiu (o rascunho
// do Novo pedido continua intacto).

interface EstadoDaBusca {
  aberta: boolean;
  abrir: () => void;
  fechar: () => void;
}

const FECHADA: EstadoDaBusca = { aberta: false, abrir: () => {}, fechar: () => {} };

const Contexto = createContext<EstadoDaBusca | null>(null);

export function ProvedorDaBusca({ children }: { children: ReactNode }) {
  const [aberta, setAberta] = useState(false);
  const abertaRef = useRef(false);
  const voltarFoco = useRef<HTMLElement | null>(null);

  const abrir = useCallback(() => {
    if (abertaRef.current) return;
    abertaRef.current = true;
    voltarFoco.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAberta(true);
  }, []);

  const fechar = useCallback(() => {
    if (!abertaRef.current) return;
    abertaRef.current = false;
    setAberta(false);
    const alvo = voltarFoco.current;
    voltarFoco.current = null;
    // Depois de a busca sair da tela (senão o foco volta e é roubado de novo).
    if (alvo) setTimeout(() => alvo.isConnected && alvo.focus(), 0);
  }, []);

  const valor = useMemo(() => ({ aberta, abrir, fechar }), [aberta, abrir, fechar]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/** Fora do provedor (testes de outras telas), a busca só não abre. */
export function useBusca(): EstadoDaBusca {
  return useContext(Contexto) ?? FECHADA;
}
